import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useCountries } from '../hooks/useCountries';

const normalizeId = (id) => String(id ?? '').trim();

const defaultGetId = (item) => normalizeId(item?.id ?? item?.country_id ?? item?.value);
const defaultGetLabel = (item) =>
  String(item?.name ?? item?.country_name ?? item?.label ?? item?.id ?? '').trim();

/**
 * Searchable single-country dropdown.
 * When `countries` is omitted, loads from GET /api/countries
 * (Vughy public countries API via VUGHY_API_KEY on the server).
 */
const CountrySelect = ({
  label,
  value = '',
  onChange,
  countries: countriesProp,
  loading: loadingProp = false,
  placeholder = 'Select country',
  emptyLabel,
  allowEmpty = true,
  disabled = false,
  required = false,
  getOptionId = defaultGetId,
  getOptionLabel = defaultGetLabel,
  className = '',
  buttonClassName = '',
  autoLoad = countriesProp === undefined,
}) => {
  const { countries: fetchedCountries, loading: fetching } = useCountries({
    enabled: autoLoad,
  });

  const countries = countriesProp !== undefined ? countriesProp : fetchedCountries;
  const loading = Boolean(loadingProp || (autoLoad && fetching));

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  const selectedId = normalizeId(value);
  const emptyOptionText = emptyLabel || placeholder;

  const options = useMemo(
    () =>
      countries
        .map((item) => ({
          id: getOptionId(item),
          label: getOptionLabel(item),
          raw: item,
        }))
        .filter((item) => item.id),
    [countries, getOptionId, getOptionLabel]
  );

  const selectedLabel = options.find((item) => item.id === selectedId)?.label || '';

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((item) => item.label.toLowerCase().includes(term));
  }, [options, search]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => {
      searchRef.current?.focus();
      searchRef.current?.select?.();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  const selectValue = (nextId) => {
    onChange(nextId);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      )}

      {required && (
        <input
          tabIndex={-1}
          aria-hidden="true"
          style={{
            position: 'absolute',
            opacity: 0,
            width: 1,
            height: 1,
            pointerEvents: 'none',
          }}
          value={selectedId}
          onChange={() => {}}
          required
        />
      )}

      <button
        type="button"
        disabled={disabled || loading}
        onClick={() => setOpen((prev) => !prev)}
        className={
          buttonClassName ||
          'w-full px-3 py-2 border border-gray-200 rounded-xl text-sm text-left bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed'
        }
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selectedLabel ? 'text-gray-900' : 'text-gray-400'}>
          {loading ? 'Loading countries...' : selectedLabel || emptyOptionText}
        </span>
      </button>

      {open && !disabled && !loading && (
        <div className="absolute z-50 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search countries..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>
          <ul role="listbox" className="max-h-56 overflow-y-auto py-1">
            {allowEmpty && (
              <li>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-gray-50"
                  onClick={() => selectValue('')}
                >
                  {emptyOptionText}
                </button>
              </li>
            )}
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-400">No countries found</li>
            )}
            {filtered.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={item.id === selectedId}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                    item.id === selectedId ? 'bg-gray-50 font-medium text-gray-900' : 'text-gray-700'
                  }`}
                  onClick={() => selectValue(item.id)}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CountrySelect;
